import { useEffect, useRef } from 'react';
import { useCurrentUser } from '../auth/CurrentUserContext';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

const PAGE_PREF_KEYS = {
  work_record: 'visited_work_record',
  reports: 'visited_reports',
  tasks: 'visited_tasks',
};

/**
 * useTrackPageVisit
 *
 * Tracks page visits for onboarding purposes.
 * Only tracks if the user is the first user and hasn't visited the page before.
 *
 * @param {string} pageName - The page identifier ('work_record' | 'reports' | 'tasks')
 * @param {boolean} [active=true] - If false, do not track (e.g. for modals, track only when open)
 */
export default function useTrackPageVisit(pageName, active = true) {
  const { user, updateCurrentUser } = useCurrentUser();
  const tracked = useRef(false);

  useEffect(() => {
    if (!active) return;
    // Only track once per mount when active
    if (tracked.current) return;

    // Only track for first users who haven't completed/dismissed onboarding
    const isFirstUser = user?.first_user;
    const onboardingPrefs = user?.preferences?.onboarding || {};
    const isOnboardingActive = !onboardingPrefs.completed_at && !onboardingPrefs.dismissed_at;

    if (!isFirstUser || !isOnboardingActive) return;

    const prefKey = PAGE_PREF_KEYS[pageName];
    if (!prefKey || onboardingPrefs[prefKey]) return;

    tracked.current = true;

    // Update preferences
    (async () => {
      try {
        const res = await fetchWithAuth(
          `${API_URL}/v1/admin/users/me/preferences`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              preferences: {
                onboarding: { [prefKey]: true }
              }
            }),
          }
        );
        if (res.ok) {
          const updatedUser = await res.json();
          updateCurrentUser(updatedUser);
        }
      } catch (error) {
        console.error('Failed to track page visit:', error);
      }
    })();
  }, [user, pageName, active, updateCurrentUser]);
}
